import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { formatInUserTime } from '../utils/date';
import QRCode from 'qrcode';
import RewardsCardModal from '../components/RewardsCardModal';

const POLL_INTERVAL_MS = 5000;
const STATUS_LABELS = {
  awaiting_payment: 'Confirming payment…',
  pending: 'Received',
  paid: 'Paid',
  in_progress: 'Preparing',
  done: 'Ready to pick up',
  picked_up: 'Picked up',
  refunded: 'Refunded',
};

/**
 * Order status page — shows live status, QR code for pickup, address when done.
 */
export default function OrderStatus() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [rewardsModal, setRewardsModal] = useState(null);
  const prevStatusRef = useRef(null);

  const fetchOrder = async () => {
    if (!orderId) return null;
    const res = await api(`/api/orders/${orderId}`, { getToken });
    if (!res.ok) return null;
    return res.json();
  };

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchOrder();
        if (cancelled) return;
        if (!data) {
          setError('Order not found.');
          return;
        }
        setOrder(data);
        if (data.receiptQrPayload) {
          try {
            const url = await QRCode.toDataURL(data.receiptQrPayload, {
              width: 200,
              margin: 2,
            });
            if (!cancelled) setQrDataUrl(url);
          } catch (e) {
            console.warn('QR generation failed', e);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load order.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    const interval = setInterval(async () => {
      const data = await fetchOrder();
      if (!cancelled && data) setOrder(data);
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [orderId, getToken]);

  useEffect(() => {
    if (!order || order.status !== 'picked_up') return;
    if (prevStatusRef.current === 'picked_up') return;
    prevStatusRef.current = 'picked_up';

    const businessId = order.businessId;
    Promise.all([
      api(`/api/orders/${order.id}/rewards-summary`, { getToken }).then((r) => (r.ok ? r.json() : null)),
      businessId ? api(`/api/customers/me/rewards?businessId=${businessId}`, { getToken }).then((r) => (r.ok ? r.json() : null)) : null,
    ]).then(([summary, rewards]) => {
      if (summary?.pointsEarned > 0) {
        setRewardsModal({
          pointsEarned: summary.pointsEarned,
          newTotalPoints: summary.newTotalPoints,
          pointsCap: summary.pointsCap,
          businessId,
          availableRewards: rewards?.availableRewards,
          canClaim: rewards?.canClaim,
          rewardsClaimedCount: rewards?.rewardsClaimedCount,
          lastClaimedRewards: rewards?.lastClaimedRewards,
        });
      }
    }).catch(() => {});
  }, [order?.id, order?.status, order?.businessId, getToken]);

  if (loading && !order) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading order status…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto py-12 px-4 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <Link
          to="/profile"
          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
        >
          Back to Dashboard
        </Link>
      </div>
    );
  }

  if (!order) return null;

  const statusLabel = STATUS_LABELS[order.status] ?? order.status;
  const isDone = order.status === 'done';
  const isReadyForPickup = isDone || order.status === 'picked_up';
  const hasAddress = order.pickupAddress?.trim();
  const mapsUrl = hasAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.pickupAddress)}`
    : null;

  return (
    <div className="max-w-md mx-auto py-8 px-4">
      <Link
        to="/profile"
        className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4 inline-block font-medium"
      >
        ← Back to Dashboard
      </Link>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
        {/* Header */}
        <div className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Order #{order.id}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            {order.businessName}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
            {formatInUserTime(order.createdAt)} · ${(order.totalCents / 100).toFixed(2)}
          </p>
        </div>

        {/* Status badge */}
        <div className="p-6">
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
              isDone
                ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'
                : order.status === 'in_progress'
                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                isDone ? 'bg-emerald-500' : order.status === 'in_progress' ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'
              }`}
            />
            {statusLabel}
          </div>

          {isDone && (
            <p className="mt-3 text-emerald-700 dark:text-emerald-300 font-medium">
              Your order is ready! Head over to pick it up.
            </p>
          )}

          {/* Address & directions when done */}
          {isReadyForPickup && hasAddress && (
            <div className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Pickup location
              </p>
              <p className="text-slate-600 dark:text-slate-400">{order.pickupAddress}</p>
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 py-2 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
                >
                  Get directions
                </a>
              )}
            </div>
          )}

          {/* QR code for pickup */}
          {qrDataUrl && !['picked_up', 'refunded'].includes(order.status) && (
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Show this QR code to the owner to mark your order as picked up
              </p>
              <div className="flex justify-center p-4 bg-white dark:bg-slate-900 rounded-xl">
                <img
                  src={qrDataUrl}
                  alt="Order pickup QR"
                  className="w-[180px] h-[180px]"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                The owner will scan this when you collect your order.
              </p>
            </div>
          )}
        </div>
      </div>

      <Link
        to={`/order/${order.id}/success`}
        className="mt-6 block text-center text-indigo-600 dark:text-indigo-400 hover:underline text-sm"
      >
        View full receipt →
      </Link>

      {rewardsModal && (
        <RewardsCardModal
          businessId={rewardsModal.businessId}
          points={rewardsModal.newTotalPoints}
          pointsCap={rewardsModal.pointsCap}
          pointsJustEarned={rewardsModal.pointsEarned}
          availableRewards={rewardsModal.availableRewards}
          canClaim={rewardsModal.canClaim}
          rewardsClaimedCount={rewardsModal.rewardsClaimedCount}
          lastClaimedRewards={rewardsModal.lastClaimedRewards}
          getToken={getToken}
          onClose={() => {
            setRewardsModal(null);
            navigate('/profile');
          }}
          showCoins
        />
      )}
    </div>
  );
}
